import React from 'react';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CustomProvider } from 'rsuite';
import { Toaster } from 'react-hot-toast';
import { store } from '../store';
import { AuthProvider } from '../contexts/AuthContext';
import { ThemeProvider, useTheme } from '../contexts/ThemeContext';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();

  return (
    <CustomProvider theme={theme}>
      <AuthProvider>
        {children}
        <Toaster
          position="top-right"
          containerStyle={{ zIndex: 1000000 }}
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--color-surface, #ffffff)',
              color: 'var(--color-gray-900, #0f172a)',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '10px',
              padding: '12px 16px',
              border: '1px solid var(--color-gray-200, #e2e8f0)',
              boxShadow: 'var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04))',
            },
            success: {
              style: {
                background: 'var(--color-surface, #ffffff)',
                color: 'var(--color-gray-900, #0f172a)',
                border: '1px solid var(--color-gray-200, #e2e8f0)',
                borderLeft: '4px solid var(--color-success, #10b981)',
              },
              iconTheme: {
                primary: 'var(--color-success, #10b981)',
                secondary: '#ffffff',
              },
            },
            error: {
              style: {
                background: 'var(--color-surface, #ffffff)',
                color: 'var(--color-gray-900, #0f172a)',
                border: '1px solid var(--color-gray-200, #e2e8f0)',
                borderLeft: '4px solid var(--color-danger, #ef4444)',
              },
              iconTheme: {
                primary: 'var(--color-danger, #ef4444)',
                secondary: '#ffffff',
              },
            },
          }}
        />
      </AuthProvider>
    </CustomProvider>
  );
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ThemedApp>{children}</ThemedApp>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  );
};
